<script setup lang="ts">
import { compareMoney, type CurrencyCode, type Money, signOfMoney } from '@taler/domain'
import { ActionButton, Dialog, Select, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import { AccountPicker } from '@/features/entries'
import { useForgive } from '../composables/use-forgive.ts'
import { useGiftsAccount } from '../composables/use-gifts-account.ts'
import type { ForgiveSubject } from '../internal/types.ts'

const props = defineProps<{ open: boolean; ledgerId: string; subject: ForgiveSubject | undefined }>()
const emit = defineEmits<{ close: []; forgiven: [] }>()
const { locale, t } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const currency = ref<CurrencyCode>()
const amount = ref<Money | null>(null)
const expenseAccountId = ref<string>()

const isContact = computed(() => props.subject?.target.kind === 'contact')
const currencyOptions = computed(() =>
  (props.subject?.owed ?? []).map((owed) => ({ value: owed.currency, label: owed.currency })),
)
const owed = computed(() => props.subject?.owed.find((candidate) => candidate.currency === currency.value))

const giftsAccount = useGiftsAccount({
  ledgerId: () => props.ledgerId,
  enabled: () => props.open && isContact.value,
})

const forgive = useForgive(() => props.ledgerId)

function selectCurrency(next: CurrencyCode | undefined): void {
  currency.value = next
  amount.value = owed.value ?? null
}

watch(
  () => props.open,
  (open) => {
    if (!open) return
    selectCurrency(props.subject?.owed[0]?.currency)
    expenseAccountId.value = undefined
    forgive.reset()
  },
  { immediate: true },
)

watch(giftsAccount, (id) => {
  if (expenseAccountId.value === undefined && id !== undefined) expenseAccountId.value = id
})

const amountError = computed(() => {
  if (amount.value === null || signOfMoney(amount.value) <= 0) return t('forgiveness.amountRequired')
  if (owed.value !== undefined && compareMoney(amount.value, owed.value) > 0) {
    return t('forgiveness.amountTooLarge')
  }
  return undefined
})

async function confirm(): Promise<void> {
  if (props.subject === undefined || amount.value === null || amountError.value !== undefined) return
  try {
    await forgive.submit({
      target: props.subject.target,
      amount: amount.value,
      ...(isContact.value && expenseAccountId.value !== undefined
        ? { expenseAccountId: expenseAccountId.value }
        : {}),
    })
    emit('forgiven')
  } catch {
    // The mutation state renders the server's error below.
  }
}
</script>

<template>
  <Dialog :open="open" :label="t('forgiveness.title')" @close="emit('close')">
    <div class="flex flex-col gap-[var(--space-group)] p-[var(--space-block)]">
      <p class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('forgiveness.title') }}
      </p>
      <p v-if="subject" class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
        {{ t('forgiveness.consequence', { name: subject.name }) }}
      </p>

      <div v-if="currencyOptions.length > 1" class="flex flex-col gap-[var(--space-hair)]">
        <label
          class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          for="forgive-currency"
        >
          {{ t('forgiveness.currency') }}
        </label>
        <Select
          id="forgive-currency"
          :model-value="currency"
          :options="currencyOptions"
          :aria-label="t('forgiveness.currency')"
          @update:model-value="selectCurrency"
        />
      </div>

      <div v-if="owed" class="flex flex-col gap-[var(--space-hair)]">
        <label
          class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          for="forgive-amount"
        >
          {{ t('forgiveness.amount') }}
        </label>
        <AmountInput
          id="forgive-amount"
          :key="owed.currency"
          v-model="amount"
          :currency="owed.currency"
          :locale="currentLocale"
          required
          :aria-label="t('forgiveness.amount')"
          :aria-invalid="amountError !== undefined"
        />
        <p v-if="amountError" class="text-[length:var(--text-step-1)] text-[color:var(--color-danger)]">
          {{ amountError }}
        </p>
      </div>

      <div v-if="isContact" class="flex flex-col gap-[var(--space-hair)]">
        <AccountPicker
          id="forgive-expense-account"
          :label="t('forgiveness.accountLabel')"
          :model-value="expenseAccountId"
          filter-class="EXPENSE"
          :ledger-id="ledgerId"
          @update:model-value="(value) => (expenseAccountId = value ?? undefined)"
        />
      </div>

      <StatusTag v-if="forgive.hasError.value" tone="danger" role="alert">
        {{ forgive.errorDetail.value ?? t('forgiveness.error') }}
      </StatusTag>

      <div class="flex justify-end gap-[var(--space-inline)]">
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-group)] text-[color:var(--color-text)]"
          @click="emit('close')"
        >
          {{ t('forgiveness.cancel') }}
        </button>
        <ActionButton
          type="button"
          :disabled="forgive.isSubmitting.value || amountError !== undefined"
          @click="confirm"
        >
          {{ t('forgiveness.confirm') }}
        </ActionButton>
      </div>
    </div>
  </Dialog>
</template>
